import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import { buildImageUrl } from '../../utils/imageUrl';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import { ArrowLeft, Plus, Edit, Trash2, CheckCircle, AlertTriangle, Package, ClipboardList, ArrowRightLeft, History, Link2, RotateCcw, Camera, X, Upload, FileDown, ChevronDown, FileText, Image, Barcode, BarChart3 } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import ChainSetupModal from './ChainSetupModal';
import PackingPartsModal from './PackingPartsModal';
import StageTrackerTab from './StageTrackerTab';
import OutsourceTab from './OutsourceTab';

const safeDate = (d) => { try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM yyyy') : '-'; } catch { return '-'; } };
const safeDateTime = (d) => { try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM, hh:mm a') : '-'; } catch { return '-'; } };
const today = () => new Date().toISOString().split('T')[0];

// ── Export Utilities ──────────────────────────────────────────────────────────
function escapeCSV(val) {
  if (val === null || val === undefined) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

function downloadCSV(csvContent, filename) {
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Product-wise Stage Tracking export
function buildProductWiseCSV(project, items, tasks, chains) {
  const rows = [];
  const pct = (comp, assign) => assign > 0 ? Math.round((comp / assign) * 100) + '%' : '0%';
  const isDelayedStr = (t) => t.status !== 'completed' && t.due_date && t.due_date < today() ? 'YES' : 'NO';

  // Header
  rows.push(['PROJECT: ' + project.name + (project.project_id ? '  [' + project.project_id + ']' : '')]);
  rows.push(['Client: ' + (project.client_name || '-') + '   Deadline: ' + (project.deadline ? safeDate(project.deadline) : '-') + '   Status: ' + (project.status || '-')]);
  rows.push(['Export Date: ' + format(new Date(), 'dd MMM yyyy, hh:mm a')]);
  rows.push([]);

  // Summary row
  const totalTasks = tasks.length;
  const doneTasks = tasks.filter(t => t.status === 'completed').length;
  const delayedTasks = tasks.filter(t => t.status !== 'completed' && t.due_date && t.due_date < today()).length;
  rows.push(['SUMMARY', 'Total Items: ' + items.length, 'Total Stages: ' + totalTasks, 'Completed: ' + doneTasks, 'Delayed: ' + delayedTasks]);
  rows.push([]);

  // Build unique stages list from chains (sorted by stage_order)
  const stageMap = {};
  chains.forEach(c => {
    const key = c.stage_order + '_' + c.department_id;
    if (!stageMap[key]) stageMap[key] = { stage_order: c.stage_order, dept_name: c.dept_name, department_id: c.department_id };
  });
  const allStages = Object.values(stageMap).sort((a, b) => a.stage_order - b.stage_order);

  // Column headers
  const stageHeaders = allStages.length > 0
    ? allStages.map(s => 'Stage ' + s.stage_order + ': ' + s.dept_name)
    : ['Stage Tasks'];
  rows.push(['Product / Item', 'Proto Code', 'Total Qty', 'Unit', 'Material', ...stageHeaders, 'Overall Progress']);

  // Per-product rows
  items.forEach(item => {
    const itemTasks = tasks.filter(t => t.project_item_id === item.id);

    if (allStages.length > 0) {
      // Stage-mapped row
      const stageCells = allStages.map(stage => {
        const t = itemTasks.find(t =>
          Number(t.stage_order) === Number(stage.stage_order) &&
          Number(t.department_id) === Number(stage.department_id)
        );
        if (!t) return 'Not assigned';
        const p = pct(t.quantity_completed || 0, t.quantity_assigned);
        const worker = t.assign_type === 'worker' ? (t.worker_name || '-') : (t.department_name || '-');
        const delayed = isDelayedStr(t) === 'YES' ? ' ⚠DELAYED' : '';
        return worker + ' | ' + (t.quantity_completed || 0) + '/' + t.quantity_assigned + ' (' + p + ') [' + t.status + ']' + delayed;
      });

      // Overall progress for this item
      const overallPct = itemTasks.length > 0
        ? Math.round(itemTasks.reduce((sum, t) => sum + (t.quantity_assigned > 0 ? (t.quantity_completed || 0) / t.quantity_assigned * 100 : 0), 0) / itemTasks.length)
        : 0;

      rows.push([
        item.item_name,
        item.proto_code || '',
        item.quantity,
        item.unit || 'pcs',
        item.material || '',
        ...stageCells,
        overallPct + '%'
      ]);
    } else {
      // No chains — flat task list for item
      if (itemTasks.length === 0) {
        rows.push([item.item_name, item.proto_code || '', item.quantity, item.unit || 'pcs', item.material || '', 'No tasks assigned', '0%']);
      } else {
        itemTasks.forEach((t, idx) => {
          const worker = t.assign_type === 'worker' ? (t.worker_name || '-') : (t.department_name || '-');
          rows.push([
            idx === 0 ? item.item_name : '',
            idx === 0 ? (item.proto_code || '') : '',
            idx === 0 ? item.quantity : '',
            idx === 0 ? (item.unit || 'pcs') : '',
            idx === 0 ? (item.material || '') : '',
            t.task_title + ' | ' + worker + ' | ' + (t.quantity_completed || 0) + '/' + t.quantity_assigned + ' (' + pct(t.quantity_completed || 0, t.quantity_assigned) + ') [' + t.status + ']',
            idx === 0 ? pct(itemTasks.reduce((s, x) => s + (x.quantity_completed || 0), 0), itemTasks.reduce((s, x) => s + (x.quantity_assigned || 0), 0)) : ''
          ]);
        });
      }
    }
  });

  rows.push([]);
  rows.push(['--- END OF REPORT ---']);

  return rows.map(r => r.map(escapeCSV).join(',')).join('\n');
}

// All Stages flat export
function buildAllStagesCSV(project, items, tasks, chains) {
  const rows = [];
  const pct = (comp, assign) => assign > 0 ? Math.round((comp / assign) * 100) + '%' : '0%';
  const isDelayedStr = (t) => t.status !== 'completed' && t.due_date && t.due_date < today() ? 'YES' : 'NO';

  rows.push(['PROJECT SUMMARY']);
  rows.push(['Project Name', project.name]);
  rows.push(['Project ID', project.project_id || '']);
  rows.push(['Client', project.client_name || '']);
  rows.push(['Status', project.status || '']);
  rows.push(['Priority', project.priority || '']);
  rows.push(['Deadline', project.deadline ? safeDate(project.deadline) : '']);
  rows.push(['Total Items', items.length]);
  rows.push(['Total Stages (Tasks)', tasks.length]);
  rows.push(['Completed Tasks', tasks.filter(t => t.status === 'completed').length]);
  rows.push(['In Progress Tasks', tasks.filter(t => t.status === 'in_progress').length]);
  rows.push(['Pending Tasks', tasks.filter(t => t.status === 'pending').length]);
  rows.push(['Delayed Tasks', tasks.filter(t => t.status !== 'completed' && t.due_date && t.due_date < today()).length]);
  rows.push(['Export Date', format(new Date(), 'dd MMM yyyy, hh:mm a')]);
  rows.push([]);

  const stageMap = {};
  chains.forEach(c => {
    const key = c.stage_order + '_' + c.department_id;
    if (!stageMap[key]) stageMap[key] = { stage_order: c.stage_order, dept_name: c.dept_name, department_id: c.department_id };
  });
  const stages = Object.values(stageMap).sort((a, b) => a.stage_order - b.stage_order);

  if (stages.length === 0) {
    rows.push(['ALL TASKS']);
    rows.push(['Task Title', 'Item', 'Assigned To', 'Status', 'Qty Assigned', 'Qty Completed', 'Progress', 'Due Date', 'Delayed', 'Priority']);
    tasks.forEach(t => {
      rows.push([
        t.task_title, t.item_name || '',
        t.assign_type === 'worker' ? (t.worker_name || '') : (t.department_name || ''),
        t.status, t.quantity_assigned, t.quantity_completed || 0,
        pct(t.quantity_completed || 0, t.quantity_assigned),
        t.due_date ? safeDate(t.due_date) : '',
        isDelayedStr(t), t.priority || ''
      ]);
    });
  } else {
    stages.forEach(stage => {
      rows.push(['STAGE ' + stage.stage_order + ': ' + stage.dept_name]);
      rows.push(['Task Title', 'Item / Product', 'Proto Code', 'Assigned To', 'Status', 'Qty Assigned', 'Qty Completed', 'Progress %', 'Start Date', 'Due Date', 'Delayed', 'Priority', 'Notes']);
      const stageTasks = tasks.filter(t =>
        Number(t.stage_order) === Number(stage.stage_order) &&
        Number(t.department_id) === Number(stage.department_id)
      );
      if (stageTasks.length === 0) {
        rows.push(['No tasks for this stage']);
      } else {
        stageTasks.forEach(t => {
          rows.push([
            t.task_title, t.item_name || '', t.proto_code || '',
            t.assign_type === 'worker' ? (t.worker_name || '') : (t.department_name || ''),
            t.status, t.quantity_assigned, t.quantity_completed || 0,
            pct(t.quantity_completed || 0, t.quantity_assigned),
            t.start_date ? safeDate(t.start_date) : '',
            t.due_date ? safeDate(t.due_date) : '',
            isDelayedStr(t), t.priority || '', t.admin_notes || ''
          ]);
        });
        const ta = stageTasks.reduce((s, t) => s + (t.quantity_assigned || 0), 0);
        const tc = stageTasks.reduce((s, t) => s + (t.quantity_completed || 0), 0);
        rows.push(['', '', '', 'STAGE TOTAL:', '', ta, tc, pct(tc, ta)]);
      }
      rows.push([]);
    });
  }

  rows.push(['ITEMS / PRODUCTS SUMMARY']);
  rows.push(['Item Name', 'Proto Code', 'Quantity', 'Unit', 'Material', 'Dimensions', 'Unit Price', 'Status']);
  items.forEach(item => {
    rows.push([item.item_name, item.proto_code || '', item.quantity, item.unit || 'pcs', item.material || '', item.dimensions || '', item.unit_price || '', item.status || '']);
  });

  return rows.map(r => r.map(escapeCSV).join(',')).join('\n');
}

function buildSingleStageCSV(project, tasks, stageName, stageOrder, departmentId) {
  const rows = [];
  const pct = (comp, assign) => assign > 0 ? Math.round((comp / assign) * 100) + '%' : '0%';
  const isDelayedStr = (t) => t.status !== 'completed' && t.due_date && t.due_date < today() ? 'YES' : 'NO';

  rows.push(['STAGE EXPORT: ' + stageName]);
  rows.push(['Project', project.name]);
  rows.push(['Client', project.client_name || '']);
  rows.push(['Export Date', format(new Date(), 'dd MMM yyyy, hh:mm a')]);
  rows.push([]);
  rows.push(['Task Title', 'Item / Product', 'Proto Code', 'Assigned To', 'Status', 'Qty Assigned', 'Qty Completed', 'Progress %', 'Start Date', 'Due Date', 'Delayed', 'Priority', 'Notes']);

  const stageTasks = tasks.filter(t =>
    Number(t.stage_order) === Number(stageOrder) &&
    Number(t.department_id) === Number(departmentId)
  );

  if (stageTasks.length === 0) {
    rows.push(['No tasks found for this stage']);
  } else {
    stageTasks.forEach(t => {
      rows.push([
        t.task_title, t.item_name || '', t.proto_code || '',
        t.assign_type === 'worker' ? (t.worker_name || '') : (t.department_name || ''),
        t.status, t.quantity_assigned, t.quantity_completed || 0,
        pct(t.quantity_completed || 0, t.quantity_assigned),
        t.start_date ? safeDate(t.start_date) : '',
        t.due_date ? safeDate(t.due_date) : '',
        isDelayedStr(t), t.priority || '', t.admin_notes || ''
      ]);
    });
    const ta = stageTasks.reduce((s, t) => s + (t.quantity_assigned || 0), 0);
    const tc = stageTasks.reduce((s, t) => s + (t.quantity_completed || 0), 0);
    rows.push([]);
    rows.push(['', '', '', 'TOTAL:', '', ta, tc, pct(tc, ta)]);
  }

  return rows.map(r => r.map(escapeCSV).join(',')).join('\n');
}
// ── MO References Section (Project level) ─────────────────────────────────────
function MoReferencesSection({ projectId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef();

  const load = () => {
    api.get(`/projects/${projectId}/mo-references`)
      .then(r => setFiles(r.data))
      .catch(() => {});
  };

  useEffect(() => { load(); }, [projectId]);

  const handleUpload = async (e) => {
    const selected = Array.from(e.target.files);
    if (!selected.length) return;
    e.target.value = '';
    setUploading(true);
    try {
      const formData = new FormData();
      selected.forEach(f => formData.append('images', f));
      await api.post(`/projects/${projectId}/mo-references`, formData);
      toast.success(`${selected.length} file(s) uploaded`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Yeh file delete karna chahte ho?')) return;
    try {
      await api.delete(`/projects/${projectId}/mo-references/${fileId}`);
      toast.success('File deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{files.length} file(s) uploaded</p>
        <button
          onClick={() => fileRef.current.click()}
          disabled={uploading}
          className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-50 transition-colors"
        >
          <Upload className="w-4 h-4" />
          {uploading ? 'Uploading...' : 'Upload Images / PDFs'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.pdf,application/pdf"
          multiple
          className="hidden"
          onChange={handleUpload}
        />
      </div>

      {files.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center">
          <Image className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">No MO references</p>
          <p className="text-gray-300 text-sm mt-1">Upload images or PDFs</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {files.map(file => {
            const isPdf = file.resource_type === 'raw' || (file.image_url && file.image_url.includes('.pdf'));
            return (
              <div key={file.id} className="relative group rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                {isPdf ? (
                  <a
                    href={file.image_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex flex-col items-center justify-center h-32 gap-2 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <FileText className="w-10 h-10 text-red-500" />
                    <span className="text-xs text-gray-500 px-2 truncate w-full text-center">PDF</span>
                  </a>
                ) : (
                  <img
                    src={file.image_url}
                    alt="mo-ref"
                    onClick={() => setLightbox(file)}
                    className="w-full h-32 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    onError={e => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                )}
                {/* Error fallback */}
                <div className="hidden w-full h-32 flex-col items-center justify-center bg-gray-100 rounded-xl">
                  <Image className="w-8 h-8 text-gray-300" />
                  <span className="text-xs text-gray-400 mt-1">Load failed</span>
                  <a href={file.image_url} target="_blank" rel="noreferrer"
                    className="text-xs text-blue-400 mt-1 underline">Open link</a>
                </div>
                {/* Delete button */}
                <button
                  onClick={() => handleDelete(file.id)}
                  className="absolute top-1.5 right-1.5 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/85 z-[60] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-4xl w-full" onClick={e => e.stopPropagation()}>
            <img
              src={lightbox.image_url}
              alt="MO Reference"
              className="w-full rounded-2xl shadow-2xl max-h-[85vh] object-contain"

            />
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-3 right-3 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Item Reference Images Component ──────────────────────────────────────────
function ItemImagesSection({ itemId, projectId, refreshKey, onUpload, onDelete, uploading }) {
  const [images, setImages] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    api.get(`/projects/${projectId}/items/${itemId}/images`)
      .then(r => setImages(r.data))
      .catch(() => {});
  }, [itemId, projectId, refreshKey]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) onUpload(files);
    e.target.value = '';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500">{images.length} image(s)</span>
        <button type="button" onClick={() => fileRef.current.click()} disabled={uploading}
          className="flex items-center gap-1 text-xs bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg font-medium disabled:opacity-50">
          <Upload className="w-3 h-3" /> {uploading ? 'Upload...' : 'Upload'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
      </div>
      {images.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center text-xs text-gray-400">
          No reference images — Upload some
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {images.map(img => (
            <div key={img.id} className="relative group">
              <img src={img.image_url} alt="ref"
                onClick={() => setLightbox(img)}
                className="w-full h-24 object-cover rounded-xl border border-gray-200 cursor-pointer group-hover:ring-2 group-hover:ring-amber-400 transition-all"
                onError={e => { e.target.style.display = 'none'; }}
              />
              <button type="button" onClick={() => onDelete(img.id)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {lightbox && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <img src={lightbox.image_url} alt="full" className="w-full rounded-2xl shadow-2xl max-h-[80vh] object-contain" />
            <button onClick={() => setLightbox(null)} className="absolute top-3 right-3 bg-white/20 hover:bg-white/40 text-white rounded-full p-1.5">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Item Reference Section (read-only, for images tab) ───────────────────────
function ItemReferenceSection({ item, projectId }) {
  const [images, setImages] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get(`/projects/${projectId}/items/${item.id}/images`)
      .then(r => setImages(r.data))
      .catch(() => {});
  }, [item.id, projectId]);

  if (images.length === 0) return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-600">{item.item_name} {item.proto_code && <span className="text-xs text-gray-400 font-mono">({item.proto_code})</span>}</span>
      <span className="text-xs text-gray-400">No images</span>
    </div>
  );

  return (
    <div className="border-b border-gray-50 last:border-0 pb-3">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between py-1">
        <span className="text-sm font-medium text-gray-800">
          {item.item_name} {item.proto_code && <span className="text-xs text-amber-600 font-mono ml-1">({item.proto_code})</span>}
        </span>
        <span className="text-xs text-blue-600">{images.length} image(s) {expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mt-2">
          {images.map(img => (
            <div key={img.id} className="cursor-pointer" onClick={() => setLightbox(img)}>
              <img src={img.image_url} alt="ref"
                className="w-full h-20 object-cover rounded-xl border border-gray-200 hover:ring-2 hover:ring-amber-400 transition-all"
                onError={e => { e.target.style.display = 'none'; }}
              />
            </div>
          ))}
        </div>
      )}
      {lightbox && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-2xl w-full">
            <img src={lightbox.image_url} alt="full" className="w-full rounded-2xl shadow-2xl max-h-[80vh] object-contain" />
            <button onClick={() => setLightbox(null)} className="absolute top-3 right-3 bg-white/20 text-white rounded-full p-1.5"><X className="w-5 h-5" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProjectDetailPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('projects');
  const { hasAccess: canSeeOutsource } = usePermission('outsource');
  const { formatDate, formatCurrency, settings } = useSettings();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/worker') ? '/worker' : '/admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [activeTab, setActiveTab] = useState('items');
  const [moRefKey, setMoRefKey] = useState(0);
  const [expandTaskId, setExpandTaskId] = useState(null);
  const [imagesByStage, setImagesByStage] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [chainModal, setChainModal] = useState(null);
  const [packingModal, setPackingModal] = useState(null);
  const [outsourceJobs, setOutsourceJobs] = useState([]);

  // Item modal
  const [showItemModal, setShowItemModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [itemForm, setItemForm] = useState({ item_name: '', description: '', quantity: 1, unit: 'pcs', material: '', dimensions: '', unit_price: '', notes: '' });
  const [itemImages, setItemImages] = useState([]);
  const [itemImgUploading, setItemImgUploading] = useState(false);

  // Qty Amendment modal
  const [amendModal, setAmendModal]         = useState(null); // { item }
  const [amendNewQty, setAmendNewQty]       = useState('');
  const [amendReason, setAmendReason]       = useState('');
  const [amendImpact, setAmendImpact]       = useState(null);  // impact from API
  const [amendLoading, setAmendLoading]     = useState(false);
  const [amendChecking, setAmendChecking]   = useState(false);

  // Task modal
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ assign_type: 'worker', worker_id: '', department_id: '', task_title: '', task_description: '', quantity_assigned: 1, priority: 'medium', start_date: today(), due_date: '', admin_notes: '', project_item_id: '', status: 'pending' });

  // Transfer modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferTask, setTransferTask] = useState(null);
  const [transferForm, setTransferForm] = useState({ to_worker_id: '', reason: '' });
  const [transferHistory, setTransferHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(null);
  const [taskPhotos, setTaskPhotos] = useState({}); // {taskId: [images]}
  const [expandedPhotos, setExpandedPhotos] = useState(null);

  // Export
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef();

  // Export handlers
  const handleExportProductWise = () => {
    if (!data) return;
    const { project, items, tasks, chains } = data;
    const csv = buildProductWiseCSV(project, items, tasks, chains || []);
    const filename = `${project.project_id || project.name}_product_stage_tracking_${format(new Date(), 'ddMMyyyy')}.csv`;
    downloadCSV(csv, filename);
    setShowExportMenu(false);
    toast.success('Product-wise Stage Tracking exported!');
  };

  const handleExportAllStages = () => {
    if (!data) return;
    const { project, items, tasks, chains } = data;
    const csv = buildAllStagesCSV(project, items, tasks, chains || []);
    const filename = `${project.project_id || project.name}_all_stages_${format(new Date(), 'ddMMyyyy')}.csv`;
    downloadCSV(csv, filename);
    setShowExportMenu(false);
    toast.success('All stages exported!');
  };

  const handleExportSingleStage = (stageName, stageOrder, departmentId) => {
    if (!data) return;
    const { project, tasks } = data;
    const csv = buildSingleStageCSV(project, tasks, stageName, stageOrder, departmentId);
    const safeName = stageName.replace(/[^a-zA-Z0-9]/g, '_');
    const filename = `${project.project_id || project.name}_stage${stageOrder}_${safeName}_${format(new Date(), 'ddMMyyyy')}.csv`;
    downloadCSV(csv, filename);
    setShowExportMenu(false);
    toast.success(`Stage ${stageOrder}: ${stageName} exported!`);
  };

  const loadTaskPhotos = async (taskId) => {
    if (taskPhotos[taskId]) {
      setExpandedPhotos(expandedPhotos === taskId ? null : taskId);
      return;
    }
    try {
      const r = await api.get(`/tasks/${taskId}/images`);
      setTaskPhotos(p => ({ ...p, [taskId]: r.data }));
      setExpandedPhotos(taskId);
    } catch { toast.error('Failed to load photos'); }
  }; // task id

  const load = async () => {
    try {
      const [projRes, workersRes, deptsRes] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get('/workers'),
        api.get('/departments', { params: { active_only: '1' } })
      ]);
      setData(projRes.data);
      setWorkers(workersRes.data.filter(w => w.is_active));
      setDepartments(deptsRes.data);
      // Load outsource jobs for this project (for Stage Tracker integration)
      api.get('/outsource-jobs', { params: { project_id: id } })
        .then(r => setOutsourceJobs(r.data || []))
        .catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // Handle task expansion from navigation
  useEffect(() => {
    if (location.state?.expandTaskId) {
      setActiveTab('tasks');
      setExpandTaskId(location.state.expandTaskId);
      // Clear state to prevent expansion on navigation back
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.expandTaskId]);

  // Scroll to expanded task
  useEffect(() => {
    if (expandTaskId) {
      setTimeout(() => {
        const taskElement = document.getElementById(`task-${expandTaskId}`);
        if (taskElement) {
          taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [expandTaskId]);

  const loadImagesByStage = async () => {
    setLoadingImages(true);
    try {
      const r = await api.get(`/projects/${id}/images-by-stage`);
      setImagesByStage(r.data);
    } catch { toast.error('Failed to load images'); }
    finally { setLoadingImages(false); }
  };

  useEffect(() => {
    if (activeTab === 'images' && imagesByStage.length === 0 && !loadingImages) {
      loadImagesByStage();
    }
  }, [activeTab]);

  // Items
  const handleItemSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) {
        await api.put(`/projects/${id}/items/${editItem.id}`, itemForm);
        toast.success('Item updated');
        setShowItemModal(false);
        setItemImages([]);
        load();
        return;
      }

      // New item — images pehle temp-upload karo, phir item create karo
      let imageUrls = [];
      if (itemImages.length > 0) {
        try {
          toast.loading('Uploading images...', { id: 'item-img-upload' });
          const formData = new FormData();
          itemImages.forEach(f => formData.append('images', f));
          const uploadRes = await api.post('/temp-upload/images', formData, {
            timeout: 60000,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          imageUrls = uploadRes.data.images || [];
          toast.dismiss('item-img-upload');
        } catch (uploadErr) {
          toast.dismiss('item-img-upload');
          toast.error('Image upload failed: ' + (uploadErr.response?.data?.message || uploadErr.message));
          return;
        }
      }

      const payload = imageUrls.length > 0 ? { ...itemForm, image_urls: imageUrls } : itemForm;
      const r = await api.post(`/projects/${id}/items`, payload);

      // 202 = approval pending
      if (r.status === 202 || r.data?.approval_status === 'PENDING') {
        toast.success(`✅ Item request submitted! Admin approval ke baad add hoga. (ID: #${r.data.approval_id})`);
        setShowItemModal(false);
        setItemImages([]);
        return;
      }

      toast.success('Item added');
      setShowItemModal(false);
      setItemImages([]);
      load();
    } catch { toast.error('Error saving item'); }
  };

  // ── Qty Amendment handlers ────────────────────────────────────────────────
  const openAmendModal = (item) => {
    setAmendModal({ item });
    setAmendNewQty(String(item.quantity));
    setAmendReason('');
    setAmendImpact(null);
  };

  const checkAmendImpact = async () => {
    if (!amendNewQty || parseInt(amendNewQty) < 1) { toast.error('Enter a valid quantity'); return; }
    if (parseInt(amendNewQty) === amendModal.item.quantity) { toast.error('New qty is same as current'); return; }
    setAmendChecking(true);
    try {
      const r = await api.get(`/projects/${id}/items/${amendModal.item.id}/qty-impact`, {
        params: { new_qty: amendNewQty }
      });
      setAmendImpact(r.data);
    } catch { toast.error('Failed to check impact'); }
    finally { setAmendChecking(false); }
  };

  const confirmAmend = async () => {
    if (!amendReason.trim()) { toast.error('Please provide a reason'); return; }
    if (!amendImpact?.can_proceed) { toast.error('Cannot proceed — check the impact warning'); return; }
    setAmendLoading(true);
    try {
      const r = await api.post(`/projects/${id}/items/${amendModal.item.id}/amend-qty`, {
        new_qty: amendNewQty,
        reason: amendReason,
      });
      toast.success(r.data.message);
      setAmendModal(null);
      setAmendImpact(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Amendment failed');
    } finally { setAmendLoading(false); }
  };
  // ─────────────────────────────────────────────────────────────────────────

  const loadItemImages = async (itemId) => {
    try {
      const r = await api.get(`/projects/${id}/items/${itemId}/images`);
      return r.data;
    } catch { return []; }
  };

  const handleItemImageUpload = async (files, itemId) => {
    if (!files.length) return;
    setItemImgUploading(true);
    try {
      const form = new FormData();
      files.forEach(f => form.append('images', f));
      await api.post(`/projects/${id}/items/${itemId}/images`, form);
      toast.success(`${files.length} image(s) uploaded ✅`);
      // Reload to show new images
      setEditItem(prev => ({ ...prev, _imgRefresh: Date.now() }));
    } catch { toast.error('Upload fail'); }
    finally { setItemImgUploading(false); }
  };

  const handleItemImageDelete = async (itemId, imageId) => {
    try {
      await api.delete(`/projects/${id}/items/${itemId}/images/${imageId}`);
      toast.success('Image deleted');
      setEditItem(prev => ({ ...prev, _imgRefresh: Date.now() }));
    } catch { toast.error('Delete failed'); }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this item?\nItems with active tasks will return an error.')) return;
    try {
      await api.delete(`/projects/${id}/items/${itemId}`);
      toast.success('Item deleted');
      load();
    } catch(err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  // Tasks
  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...taskForm, project_id: parseInt(id) };
      if (payload.assign_type === 'worker') payload.department_id = null;
      else payload.worker_id = null;
      if (editTask) {
        await api.put(`/tasks/${editTask.id}`, payload);
        toast.success('Task updated');
      } else {
        await api.post('/tasks', payload);
        toast.success('Task assigned');
      }
      setShowTaskModal(false);
      load();
    } catch { toast.error('Error saving task'); }
  };

  const handleRevertProgress = async (task) => {
    if (!window.confirm(`Reset progress for "${task.task_title}" to 0?\nStatus will revert to pending.`)) return;
    try {
      await api.patch(`/tasks/${task.id}/progress`, {
        quantity_completed: 0,
        status: 'pending',
        worker_notes: ''
      });
      toast.success('Progress reverted to 0 ✅');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Revert failed');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success('Task deleted successfully');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete task');
    }
  };

  const openNewTask = (item = null) => {
    setEditTask(null);
    setTaskForm({ assign_type: 'worker', worker_id: '', department_id: '', task_title: item ? `${item.item_name} - Task` : '', task_description: '', quantity_assigned: item ? item.quantity : 1, priority: 'medium', start_date: today(), due_date: data?.project?.deadline || '', admin_notes: '', project_item_id: item?.id || '', status: 'pending' });
    setShowTaskModal(true);
  };

  // Open edit task — pre-fill from existing task data
  const openEditTask = (task) => {
    setEditTask(task);
    setTaskForm({
      assign_type: task.assign_type || 'worker',
      worker_id: task.worker_id || '',
      department_id: task.department_id || '',
      task_title: task.task_title || '',
      task_description: task.task_description || '',
      quantity_assigned: task.quantity_assigned || 1,
      priority: task.priority || 'medium',
      start_date: task.start_date ? task.start_date.split('T')[0] : today(),
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      admin_notes: task.admin_notes || '',
      project_item_id: task.project_item_id || '',
      status: task.status || 'pending',
    });
    setShowTaskModal(true);
  };

  // Transfer
  const openTransfer = (task) => {
    setTransferTask(task);
    setTransferForm({ to_worker_id: '', reason: '' });
    setShowTransferModal(true);
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!transferForm.to_worker_id) return toast.error('Please select a new worker');
    try {
      await api.post(`/tasks/${transferTask.id}/transfer`, transferForm);
      toast.success('Task transferred! ✅');
      setShowTransferModal(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.message || 'Transfer failed'); }
  };

  const loadTransferHistory = async (taskId) => {
    if (showHistory === taskId) { setShowHistory(null); return; }
    try {
      const r = await api.get(`/tasks/${taskId}`);
      setTransferHistory(r.data.transfers || []);
      setShowHistory(taskId);
    } catch { toast.error('Failed to load history'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" /></div>;
  if (!data) return <div className="text-center py-20 text-gray-500">Project not found</div>;

  const { project, items, tasks } = data;
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  
  // CORRECT Progress Calculation: Item-based, not task-based
  // Each item goes through multiple stages, so sum items not tasks
  const totalItemQty = items.reduce((s, i) => s + (i.quantity || 0), 0);
  
  // Progress = completed tasks / total tasks (matches dashboard calculation)
  const itemCompletion = items.map(item => {
    const itemTasks = tasks.filter(t => t.project_item_id === item.id && (t.quantity_assigned || 0) > 0);
    if (itemTasks.length === 0) return 0;
    const avgPct = itemTasks.reduce((sum, t) => sum + Math.round((t.quantity_completed || 0) / t.quantity_assigned * 100), 0) / itemTasks.length;
    return Math.round(avgPct * item.quantity / 100);
  });

  const completedItemQty = itemCompletion.reduce((s, q) => s + q, 0);
  const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate(`${basePath}/projects`)} className="modal-close transition-colors mt-1">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
            <span className="text-sm font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">{project.project_id}</span>
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize status-${project.status}`}>{project.status}</span>
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize priority-${project.priority}`}>{project.priority}</span>
            {project.proto_mode && (
              <span title="Proto Code mode — set by the Manufacturing Order and fixed for this Project"
                className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${project.proto_mode === 'auto' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-slate-50 text-slate-700 border-slate-300'}`}>
                # Proto Code: {project.proto_mode === 'auto' ? 'Automatic' : 'Manual'}
              </span>
            )}
          </div>
          <p className="text-gray-500 text-sm mt-1">{project.client_name && `Client: ${project.client_name}`} {project.deadline && `· Deadline: ${formatDate(project.deadline)}`}</p>
        </div>
        {/* Export Button */}
        <div className="relative flex-shrink-0" ref={exportMenuRef}>
          <button
            onClick={() => setShowExportMenu(v => !v)}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors shadow-sm"
          >
            <FileDown className="w-4 h-4" />
            Export
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showExportMenu ? 'rotate-180' : ''}`} />
          </button>
          {showExportMenu && (
            <div className="absolute right-0 top-full mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 z-[9999] min-w-[260px] overflow-hidden" style={{boxShadow:'0 20px 60px rgba(0,0,0,0.15)'}}>
              <div className="px-4 py-3 border-b bg-gray-50">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Export Options</p>
              </div>
              {/* Product-wise Stage Tracking — primary export */}
              <button
                onClick={handleExportProductWise}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-amber-50 text-left transition-colors border-b border-gray-100"
              >
                <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Package className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Product-wise Stage Tracking</p>
                  <p className="text-xs text-gray-500">All stages for each product in one row</p>
                </div>
              </button>
              {/* All Stages flat export */}
              <button
                onClick={handleExportAllStages}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-green-50 text-left transition-colors border-b border-gray-100"
              >
                <div className="w-8 h-8 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  <FileDown className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">All Stages Export</p>
                  <p className="text-xs text-gray-500">Stage-by-stage breakdown</p>
                </div>
              </button>
              {data?.chains && (() => {
                const stageMap = {};
                data.chains.forEach(c => {
                  const key = c.stage_order + '_' + c.department_id;
                  if (!stageMap[key]) stageMap[key] = { stage_order: c.stage_order, dept_name: c.dept_name, dept_color: c.dept_color, department_id: c.department_id };
                });
                const stages = Object.values(stageMap).sort((a, b) => a.stage_order - b.stage_order);
                if (stages.length === 0) return (
                  <div className="px-4 py-3 text-xs text-gray-400 text-center">No stages set — add a production chain first</div>
                );
                return stages.map(stage => (
                  <button
                    key={stage.stage_order + '_' + stage.department_id}
                    onClick={() => handleExportSingleStage(stage.dept_name, stage.stage_order, stage.department_id)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-left transition-colors border-b border-gray-50 last:border-0"
                  >
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xs font-bold"
                      style={{ backgroundColor: stage.dept_color || '#6b7280' }}>
                      {stage.stage_order}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{stage.dept_name}</p>
                      <p className="text-xs text-gray-400">Stage {stage.stage_order} only</p>
                    </div>
                  </button>
                ));
              })()}
            </div>
          )}
          {showExportMenu && (
            <div className="fixed inset-0 z-[9998]" onClick={() => setShowExportMenu(false)} />
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-gray-700">Overall Progress</span>
          <span className="text-2xl font-bold text-amber-600">{progress}%</span>
        </div>
        <div className="bg-gray-100 rounded-full h-3">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 h-3 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex gap-6 mt-3 text-sm">
          <span className="text-green-600 font-medium">✓ {items.filter(i => itemCompletion[items.indexOf(i)] > 0).length}/{items.length} Items Done</span>
          <span className="text-amber-600 font-medium">{completedItemQty}/{totalItemQty} Units Complete</span>
          <span className="text-blue-600 font-medium">{tasks.filter(t => t.status === 'in_progress').length} Stages In Progress</span>
          <span className="text-yellow-600 font-medium">{tasks.filter(t => t.status === 'pending').length} Stages Pending</span>
          <span className="text-red-600 font-medium">{tasks.filter(t => t.status !== 'completed' && t.due_date && t.due_date < today()).length} Delayed</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-none">
        <div className="flex gap-2 border-b border-gray-200 min-w-max sm:min-w-0">
        {[['items', 'Items / Products', Package], ['tasks', 'Task Assignments', ClipboardList], ['stage-tracker', 'Stage Tracker', BarChart3], ['images', 'Images by Stage', Camera], ['mo-refs', 'MO References', FileText], ...(canSeeOutsource ? [['outsource', 'Outsource', null]] : [])].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setActiveTab(key)} className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${activeTab === key ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {Icon && <Icon className="w-4 h-4" />}
            {key === 'outsource' && <span>🚚</span>}
            {label}
            {(key === 'items' || key === 'tasks') && <span className="bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded-full">{key === 'items' ? items.length : tasks.length}</span>}
          </button>
        ))}
        </div>
      </div>

      {/* Items Tab */}
      {activeTab === 'items' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-gray-900 flex-1 min-w-0">Order Items / Products</h3>
            <button onClick={() => { setEditItem(null); setItemForm({ item_name: '', proto_code: '', description: '', quantity: 1, unit: 'pcs', material: '', dimensions: '', unit_price: '', notes: '' }); setShowItemModal(true); }} className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-xl text-sm font-medium transition-colors whitespace-nowrap">
              <Plus className="w-4 h-4" /> Add Item
            </button>
            <button onClick={() => setChainModal({ item: null })} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-xl text-sm font-medium transition-colors whitespace-nowrap">
              <Link2 className="w-4 h-4" /> Set Chain
            </button>
          </div>

          {(() => {
            const noChain = items.filter(it => !(data?.chains || []).some(c => c.project_item_id === it.id));
            const lateNew = noChain.filter(it => Number(it.added_from_mo_later) === 1);
            if (!noChain.length) return null;
            return (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                <Link2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  {lateNew.length
                    ? <><strong>New items have been added to the Project.</strong> Please complete chain setting and other required configurations. </>
                    : null}
                  {noChain.length} item{noChain.length === 1 ? '' : 's'} {noChain.length === 1 ? 'has' : 'have'} no production chain yet — marked <em>Needs chain setting</em> below.
                </span>
              </div>
            );
          })()}

          {items.length === 0 ? (
            <div className="bg-white rounded-2xl p-10 text-center border border-dashed border-gray-200">
              <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No items added yet. Add products to this order.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {items.map(item => (
                <div key={item.id} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {/* Item Thumbnail */}
                      {item.thumbnail_path ? (
                        item.thumbnail_type === 'raw' ? (
                          <a href={item.thumbnail_path} target="_blank" rel="noreferrer"
                            className="flex-shrink-0 w-12 h-12 bg-red-50 border border-red-200 rounded-xl flex items-center justify-center hover:bg-red-100 transition-colors"
                            title="PDF Download">
                            <FileText className="w-6 h-6 text-red-500" />
                          </a>
                        ) : (
                          <img src={item.thumbnail_path} alt={item.item_name}
                            className="flex-shrink-0 w-12 h-12 object-cover rounded-xl border border-gray-200 cursor-pointer hover:ring-2 hover:ring-amber-400 transition-all"
                            onError={e => { e.target.style.display='none'; }}
                            onClick={() => window.open(item.thumbnail_path, '_blank')}
                          />
                        )
                      ) : (
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center border border-gray-200">
                          <Package className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-900">{item.item_name}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium status-${item.status}`}>{item.status}</span>
                          <span className="text-sm text-gray-600 font-medium">{item.quantity} {item.unit}</span>
                          {item.proto_code && <span className="text-xs font-mono text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">{item.proto_code}</span>}
                          {Number(item.added_from_mo_later) === 1 && <span className="text-[11px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-full">Added from Manufacturing Order</span>}
                          {!(data?.chains || []).some(c => c.project_item_id === item.id) && <span className="text-[11px] font-bold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">Needs chain setting</span>}
                          {item.material && <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{item.material}</span>}
                          {item.dimensions && <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{item.dimensions}</span>}
                        </div>
                        {item.description && <p className="text-sm text-gray-500 mt-1">{item.description}</p>}
                        {item.unit_price > 0 && <p className="text-sm text-gray-600 mt-1">{formatCurrency(item.unit_price)}/unit</p>}
                        {item.created_by_name && (
                          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-blue-50 border border-blue-100 rounded-full text-xs font-semibold text-blue-700">
                            ✏️ Created by: {item.created_by_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-3">
                      <button onClick={() => setChainModal({ item })} title="Set production chain - tasks auto-create" className="p-1.5 hover:bg-purple-100 rounded-lg text-purple-600 font-semibold border border-purple-300"><Link2 className="w-4 h-4" /></button>
                      {settings.packing_mode !== 'manual' && (
                        <button onClick={() => setPackingModal(item)} title="Packing parts setup" className="p-1.5 hover:bg-purple-100 rounded-lg text-purple-600 border border-purple-200"><Package className="w-4 h-4" /></button>
                      )}
                      {/* Amend Qty button — only show if item has chain tasks */}
                      {data?.chains?.some(c => c.project_item_id === item.id) && (
                        <button
                          onClick={() => openAmendModal(item)}
                          title="Amend quantity (chain-safe)"
                          className="p-1.5 hover:bg-blue-100 rounded-lg text-blue-600 border border-blue-200"
                        >
                          <span className="text-xs font-bold px-1">±Qty</span>
                        </button>
                      )}
                      <button onClick={() => { setEditItem(item); setItemForm({ item_name: item.item_name, proto_code: item.proto_code||'', description: item.description||'', quantity: item.quantity, unit: item.unit, material: item.material||'', dimensions: item.dimensions||'', unit_price: item.unit_price||'', notes: item.notes||'', status: item.status }); setShowItemModal(true); }} className="p-1.5 hover:bg-amber-100 rounded-lg text-amber-600"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDeleteItem(item.id)} className="p-1.5 hover:bg-red-100 rounded-lg text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stage Tracker Tab */}
      {activeTab === 'stage-tracker' && (
        <StageTrackerTab
          projectId={id}
          items={items}
          tasks={tasks}
          chains={data?.chains || []}
          outsourceJobs={outsourceJobs}
        />
      )}

      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Task Assignments</h3>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800 flex items-start gap-2">
            <span className="text-base flex-shrink-0">ℹ️</span>
            <span><strong>Auto-created:</strong> Tasks are automatically created when you set a chain for an item. Each stage in the chain automatically gets a task assignment.</span>
          </div>

          {tasks.length === 0 ? (
            <div className="bg-white rounded-2xl p-10 text-center border border-dashed border-gray-200">
              <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No tasks yet</p>
              <p className="text-sm text-gray-400 mt-2">Set a chain for an item to auto-create tasks for each stage.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {tasks.map(task => {
                const isDelayed = task.status !== 'completed' && task.due_date && task.due_date < today();
                const pct = task.quantity_assigned > 0 ? Math.round((task.quantity_completed / task.quantity_assigned) * 100) : 0;
                return (
                  <div key={task.id} className={`bg-white rounded-2xl p-4 shadow-sm border transition-all ${expandTaskId === task.id ? 'border-blue-500 border-2 bg-blue-50 ring-2 ring-blue-200' : isDelayed ? 'border-red-200 bg-red-50/30' : 'border-gray-100'}`} id={`task-${task.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {isDelayed && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                          <p className="font-semibold text-gray-900">{task.task_title}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium status-${task.status}`}>{task.status}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium priority-${task.priority}`}>{task.priority}</span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500 mt-1">
                          {task.assign_type === 'worker' && task.worker_name && (
                            <span className="flex items-center gap-1">👤 {task.worker_name}</span>
                          )}
                          {task.assign_type === 'department' && task.department_name && (
                            <span className="flex items-center gap-1 font-medium" style={{ color: task.dept_color }}>🏭 {task.department_name}</span>
                          )}
                          {task.item_name && <span>📦 {task.item_name}</span>}
                          {task.due_date && <span className={isDelayed ? 'text-red-500 font-medium' : ''}>📅 Due: {formatDate(task.due_date)}</span>}
                          <span>✅ {task.quantity_completed}/{task.quantity_assigned}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex-1 bg-gray-100 rounded-full h-1.5 max-w-48">
                            <div className={`h-1.5 rounded-full transition-all ${task.status === 'completed' ? 'bg-green-500' : isDelayed ? 'bg-red-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-gray-500">{pct}%</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => openTransfer(task)} title="Transfer task" className="p-1.5 hover:bg-purple-100 rounded-lg text-purple-600"><ArrowRightLeft className="w-4 h-4" /></button>
                        <button onClick={() => loadTransferHistory(task.id)} title="Transfer history" className="p-1.5 hover:bg-blue-100 rounded-lg text-blue-500"><History className="w-4 h-4" /></button>
                        <button onClick={() => openEditTask(task)} className="p-1.5 hover:bg-amber-100 rounded-lg text-amber-600"><Edit className="w-4 h-4" /></button>
                        {task.quantity_completed > 0 && task.status !== 'completed' && (
                          <button onClick={() => handleRevertProgress(task)} title="Reset progress to 0 (Revert)"
                            className="p-1.5 hover:bg-orange-100 rounded-lg text-orange-500">
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                        {task.status === 'completed' && (
                          <button onClick={() => handleRevertProgress(task)} title="Revert to pending"
                            className="p-1.5 hover:bg-orange-100 rounded-lg text-orange-500">
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => loadTaskPhotos(task.id)} title="View photos"
                          className={`p-1.5 rounded-lg transition-colors ${expandedPhotos === task.id ? 'bg-blue-100 text-blue-600' : 'hover:bg-blue-100 text-blue-400'}`}>
                          <Camera className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteTask(task.id)} className="p-1.5 hover:bg-red-100 rounded-lg text-red-500"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    {/* Task Photos — Admin View */}
                    {expandedPhotos === task.id && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1.5">
                          <Camera className="w-3.5 h-3.5" /> Worker Photos
                        </p>
                        {!taskPhotos[task.id]?.length ? (
                          <p className="text-xs text-gray-400 bg-gray-50 rounded-lg p-3 text-center">No photos uploaded yet</p>
                        ) : (
                          <div className="grid grid-cols-4 gap-2">
                            {taskPhotos[task.id].map(img => (
                              <div key={img.id} className="relative group">
                                <a href={img.image_url || buildImageUrl(img.image_path)} target="_blank" rel="noreferrer">
                                  <img
                                    src={img.image_url || buildImageUrl(img.image_path)}
                                    alt="task"
                                    className="w-full h-20 object-cover rounded-xl border border-gray-200 group-hover:opacity-90 transition-opacity cursor-pointer"
                                  />
                                </a>
                                <div className="mt-0.5 text-xs text-gray-400 truncate px-0.5">
                                  {img.uploaded_by_name}
                                  <span className="text-gray-300 ml-1">{img.image_type}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Transfer History inline */}
                    {showHistory === task.id && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Transfer History</p>
                        {transferHistory.length === 0 ? (
                          <p className="text-xs text-gray-400">No transfer history</p>
                        ) : transferHistory.map(t => (
                          <div key={t.id} className="flex items-center gap-2 text-xs text-gray-600 mb-1.5 bg-purple-50 rounded-lg px-3 py-2">
                            <ArrowRightLeft className="w-3 h-3 text-purple-500 flex-shrink-0" />
                            <span className="font-medium text-purple-700">{t.from_worker_name || '?'}</span>
                            <span>→</span>
                            <span className="font-medium text-green-700">{t.to_worker_name || '?'}</span>
                            <span className="text-gray-400 mx-1">|</span>
                            <span className="text-gray-500">By: {t.transferred_by_name}</span>
                            <span className="text-gray-400 mx-1">|</span>
                            <span className="text-gray-400">{safeDateTime(t.created_at)}</span>
                            {t.reason && <span className="text-gray-500 italic">— {t.reason}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Images by Stage Tab */}
      {activeTab === 'images' && (
        <div className="space-y-4">

          {/* Reference Images per Item */}
          {data?.items?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b bg-amber-50">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  🖼️ Reference Images (Admin uploaded)
                </h3>
              </div>
              <div className="modal-body space-y-4">
                {data.items.map(item => (
                  <ItemReferenceSection key={item.id} item={item} projectId={id} />
                ))}
              </div>
            </div>
          )}

          {/* Worker uploaded images — project-wise: item → stage */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Camera className="w-4 h-4" /> Worker Photos (Item &amp; Stage wise)
              </h3>
              {!loadingImages && imagesByStage.length > 0 && (
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                  {imagesByStage.length} item{imagesByStage.length !== 1 ? 's' : ''} with photos
                </span>
              )}
            </div>
            {loadingImages ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
              </div>
            ) : imagesByStage.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <Camera className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm">No worker photos yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {imagesByStage.map((itemGroup, iIdx) => (
                  <div key={iIdx}>
                    {/* Item header */}
                    <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-lg bg-amber-200 text-amber-800 text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {iIdx + 1}
                      </span>
                      <span className="font-semibold text-gray-800 text-sm">{itemGroup.item_name}</span>
                      {itemGroup.proto_code && (
                        <span className="text-xs font-mono bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                          {itemGroup.proto_code}
                        </span>
                      )}
                    </div>

                    {/* Stages for this item */}
                    {itemGroup.stages.filter(s => s.images.length > 0).map((stage, sIdx) => (
                      <div key={sIdx} className="border-b border-gray-50 last:border-0 ml-4">
                        <div
                          className="px-4 py-2.5 border-b border-gray-100"
                          style={{ backgroundColor: (stage.dept_color || '#6B7280') + '15' }}
                        >
                          <h4 className="font-medium text-gray-700 text-xs flex items-center gap-1.5">
                            <span
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: stage.dept_color || '#9CA3AF' }}
                            />
                            Stage {stage.stage_order}: {stage.dept_name}
                            <span className="text-gray-400 ml-1">({stage.images.length} photo{stage.images.length !== 1 ? 's' : ''})</span>
                          </h4>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 p-4">
                          {stage.images.map(img => (
                            <div key={img.id} className="group">
                              <a href={img.image_url} target="_blank" rel="noreferrer">
                                <img
                                  src={img.image_url}
                                  alt={img.task_title}
                                  className="w-full h-24 object-cover rounded-xl border border-gray-200 group-hover:opacity-90 transition-opacity shadow-sm"
                                />
                              </a>
                              <div className="mt-1.5 text-xs">
                                <p className="font-medium text-gray-700 truncate">{img.task_title}</p>
                                <p className="text-gray-400 truncate">📸 {img.uploaded_by_name}</p>
                                {img.caption && <p className="text-gray-400 truncate italic">{img.caption}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* MO References Tab */}
      {activeTab === 'mo-refs' && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-5">
            <FileText className="w-5 h-5 text-amber-600" />
            <h3 className="font-bold text-gray-900">MO Reference Images & PDFs</h3>
          </div>
          <MoReferencesSection projectId={id} />
        </div>
      )}

      {/* Outsource Tab */}
      {activeTab === 'outsource' && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <OutsourceTab projectId={id} items={items} />
        </div>
      )}

      {/* Item Modal */}
      {showItemModal && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:560}}>
            <div className="modal-header">
              <h2 className="text-lg font-bold">{editItem ? 'Edit Item' : 'Add Item'}</h2>
              <button onClick={() => setShowItemModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <form onSubmit={handleItemSubmit} className="modal-body space-y-4">
              <div>
                <label className="field-label">Item Name *</label>
                <input required value={itemForm.item_name} onChange={e => setItemForm({...itemForm, item_name: e.target.value})} className="field-input" placeholder="e.g. Wardrobe, Dining Table" />
              </div>
              <div>
                <label className="field-label">Proto Code{project?.proto_mode === 'auto' ? ' (Automatic)' : ''}</label>
                {project?.proto_mode === 'auto'
                  ? <input value={editItem ? (itemForm.proto_code || '') : ''} disabled className="field-input bg-gray-50 text-purple-700"
                      placeholder="Next code of the series is given on save" title="This Project uses Automatic Proto Codes" />
                  : <input value={itemForm.proto_code||''} onChange={e => setItemForm({...itemForm, proto_code: e.target.value})} className="field-input" placeholder="e.g. MJ-5086" maxLength={100} />}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="field-label">Quantity *</label>
                  <input type="number" min="1" required value={itemForm.quantity} onChange={e => setItemForm({...itemForm, quantity: e.target.value})} className="field-input" />
                </div>
                <div>
                  <label className="field-label">Unit</label>
                  <select value={itemForm.unit} onChange={e => setItemForm({...itemForm, unit: e.target.value})} className="field-input">
                    {['pcs','set','pair','unit','dozen'].map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Material</label>
                  <input value={itemForm.material} onChange={e => setItemForm({...itemForm, material: e.target.value})} className="field-input" placeholder="Teak, Pine, MDF..." />
                </div>
                <div>
                  <label className="field-label">Dimensions</label>
                  <input value={itemForm.dimensions} onChange={e => setItemForm({...itemForm, dimensions: e.target.value})} className="field-input" placeholder='6x3x2 ft' />
                </div>
                <div>
                  <label className="field-label">Unit Price (₹)</label>
                  <input type="number" value={itemForm.unit_price} onChange={e => setItemForm({...itemForm, unit_price: e.target.value})} className="field-input" placeholder="0" />
                </div>
                {editItem && (
                  <div>
                    <label className="field-label">Status</label>
                    <select value={itemForm.status} onChange={e => setItemForm({...itemForm, status: e.target.value})} className="field-input">
                      {['pending','in_progress','completed'].map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label className="field-label">Description</label>
                <textarea value={itemForm.description} onChange={e => setItemForm({...itemForm, description: e.target.value})} rows={2} className="field-input" style={{resize:"none"}} />
              </div>

              {/* Reference Images */}
              <div>
                <label className="field-label">Reference Images <span className="text-gray-400 font-normal">(design, drawing, sample)</span></label>
                {editItem ? (
                  <ItemImagesSection
                    itemId={editItem.id}
                    projectId={id}
                    refreshKey={editItem._imgRefresh}
                    onUpload={(files) => handleItemImageUpload(files, editItem.id)}
                    onDelete={(imageId) => handleItemImageDelete(editItem.id, imageId)}
                    uploading={itemImgUploading}
                  />
                ) : (
                  <div>
                    <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl p-4 cursor-pointer hover:border-amber-400 hover:bg-amber-50 transition-colors text-sm text-gray-500">
                      <span>📎</span>
                      <span>Select images (will be uploaded after saving the item)</span>
                      <input type="file" accept="image/*" multiple className="hidden"
                        onChange={e => setItemImages(Array.from(e.target.files))} />
                    </label>
                    {itemImages.length > 0 && (
                      <p className="text-xs text-green-600 mt-1 font-medium">✅ {itemImages.length} image(s) ready — will upload on save</p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-2.5 rounded-xl text-sm font-medium">{editItem ? 'Update' : 'Add Item'}</button>
                <button type="button" onClick={() => { setShowItemModal(false); setItemImages([]); }} className="flex-1 border border-gray-200 text-gray-600 hover:bg-gray-50 py-2.5 rounded-xl text-sm font-medium">Cancel</button>
              </div>
            </form>
          </div>
            </div>
        </div>
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:560}}>
            <div className="modal-header">
              <h2 className="text-lg font-bold">{editTask ? 'Edit Task' : 'Assign Task'}</h2>
              <button onClick={() => setShowTaskModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <form onSubmit={handleTaskSubmit} className="modal-body space-y-4">
              <div>
                <label className="field-label">Task Title *</label>
                <input required value={taskForm.task_title} onChange={e => setTaskForm({...taskForm, task_title: e.target.value})} className="field-input" placeholder="e.g. Cut wood for wardrobe" />
              </div>

              {/* Assign Type Toggle */}
              <div>
                <label className="field-label">Assign To</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setTaskForm({...taskForm, assign_type: 'worker'})} className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors ${taskForm.assign_type === 'worker' ? 'bg-amber-600 text-white border-amber-600' : 'border-gray-200 text-gray-600'}`}>👤 Worker</button>
                  <button type="button" onClick={() => setTaskForm({...taskForm, assign_type: 'department'})} className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors ${taskForm.assign_type === 'department' ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600'}`}>🏭 Department</button>
                </div>
              </div>

              {taskForm.assign_type === 'worker' ? (
                <div>
                  <label className="field-label">Select Worker *</label>
                  <select required={taskForm.assign_type === 'worker'} value={taskForm.worker_id} onChange={e => setTaskForm({...taskForm, worker_id: e.target.value})} className="field-input">
                    <option value="">-- Select Worker --</option>
                    {workers.map(w => <option key={w.id} value={w.id}>{w.name} {w.department_names ? `(${w.department_names})` : ''}</option>)}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="field-label">Select Department *</label>
                  <select required={taskForm.assign_type === 'department'} value={taskForm.department_id} onChange={e => setTaskForm({...taskForm, department_id: e.target.value})} className="field-input">
                    <option value="">-- Select Department --</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="field-label">Linked Item (Optional)</label>
                <select value={taskForm.project_item_id} onChange={e => setTaskForm({...taskForm, project_item_id: e.target.value})} className="field-input">
                  <option value="">-- No specific item --</option>
                  {items.map(item => <option key={item.id} value={item.id}>{item.item_name} (Qty: {item.quantity})</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="field-label">Quantity *</label>
                  <input type="number" min="1" required value={taskForm.quantity_assigned} onChange={e => setTaskForm({...taskForm, quantity_assigned: e.target.value})} className="field-input" />
                </div>
                <div>
                  <label className="field-label">Priority</label>
                  <select value={taskForm.priority} onChange={e => setTaskForm({...taskForm, priority: e.target.value})} className="field-input">
                    {['low','medium','high','urgent'].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Start Date</label>
                  <input type="date" value={taskForm.start_date} onChange={e => setTaskForm({...taskForm, start_date: e.target.value})} className="field-input" />
                </div>
                <div>
                  <label className="field-label">Due Date</label>
                  <input type="date" value={taskForm.due_date} onChange={e => setTaskForm({...taskForm, due_date: e.target.value})} className="field-input" />
                </div>
              </div>

              {editTask && (
                <div>
                  <label className="field-label">Status</label>
                  <select value={taskForm.status} onChange={e => setTaskForm({...taskForm, status: e.target.value})} className="field-input">
                    {['pending','in_progress','completed','on_hold'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="field-label">Task Description</label>
                <textarea value={taskForm.task_description} onChange={e => setTaskForm({...taskForm, task_description: e.target.value})} rows={2} className="field-input" style={{resize:"none"}} placeholder="Detailed instructions..." />
              </div>
              <div>
                <label className="field-label">Admin Notes</label>
                <textarea value={taskForm.admin_notes} onChange={e => setTaskForm({...taskForm, admin_notes: e.target.value})} rows={2} className="field-input" style={{resize:"none"}} placeholder="Internal notes..." />
              </div>

              <div className="flex gap-3">
                <button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-2.5 rounded-xl text-sm font-medium">{editTask ? 'Update Task' : 'Assign Task'}</button>
                <button type="button" onClick={() => setShowTaskModal(false)} className="flex-1 border border-gray-200 text-gray-600 hover:bg-gray-50 py-2.5 rounded-xl text-sm font-medium">Cancel</button>
              </div>
            </form>
          </div>
            </div>
        </div>
      )}

      {/* Chain Setup Modal */}
      {chainModal && data && (
        <ChainSetupModal
          project={data.project}
          item={chainModal.item}
          onClose={() => setChainModal(null)}
          onSaved={() => { setChainModal(null); load(); }}
        />
      )}

      {/* Packing Parts Modal */}
      {packingModal && data && (
        <PackingPartsModal
          project={data.project}
          item={packingModal}
          onClose={() => setPackingModal(null)}
          onSaved={() => { setPackingModal(null); toast.success('Packing parts saved!'); }}
        />
      )}

      {/* Transfer Modal */}
      {showTransferModal && transferTask && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:480}}>
            <div className="modal-header">
              <div>
                <h2 className="text-lg font-bold flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-purple-600" /> Transfer Task</h2>
                <p className="text-sm text-gray-500 mt-0.5">"{transferTask.task_title}"</p>
              </div>
              <button onClick={() => setShowTransferModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <form onSubmit={handleTransfer} className="modal-body space-y-4">
              {/* Current assignee */}
              <div className="bg-gray-50 rounded-xl p-3 text-sm">
                <p className="text-xs text-gray-500 mb-1">Currently assigned to</p>
                <p className="font-semibold text-gray-800">{transferTask.worker_name || transferTask.department_name || 'Unassigned'}</p>
                <p className="text-xs text-gray-500 mt-1">Progress: {transferTask.quantity_completed}/{transferTask.quantity_assigned} ({transferTask.quantity_assigned > 0 ? Math.round(transferTask.quantity_completed / transferTask.quantity_assigned * 100) : 0}%)</p>
              </div>

              <div>
                <label className="field-label">Select New Worker *</label>
                <select
                  required
                  value={transferForm.to_worker_id}
                  onChange={e => setTransferForm({ ...transferForm, to_worker_id: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">-- Select a worker --</option>
                  {workers.filter(w => w.id !== transferTask.worker_id).map(w => (
                    <option key={w.id} value={w.id}>{w.name} {w.department_names ? `(${w.department_names})` : ''}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="field-label">Reason for Transfer (optional)</label>
                <textarea
                  value={transferForm.reason}
                  onChange={e => setTransferForm({ ...transferForm, reason: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  placeholder="e.g. Worker is unavailable, too much workload..."
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                ⚠️ Transferring will unassign from the current worker and assign to the new worker. Progress will be maintained.
              </div>

              <div className="flex gap-3">
                <button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2.5 rounded-xl text-sm font-semibold">
                  🔄 Transfer Task
                </button>
                <button type="button" onClick={() => setShowTransferModal(false)} className="flex-1 border border-gray-200 text-gray-600 hover:bg-gray-50 py-2.5 rounded-xl text-sm font-medium">
                  Cancel
                </button>
              </div>
            </form>
          </div>
            </div>
        </div>
      )}

      {/* ── Qty Amendment Modal ─────────────────────────────────────────── */}
      {amendModal && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{ maxWidth: 520 }}>
              <div className="modal-header">
                <h2 className="text-lg font-bold">⚖️ Amend Quantity — {amendModal.item.item_name}</h2>
                <button onClick={() => { setAmendModal(null); setAmendImpact(null); }} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
              </div>
              <div className="modal-body space-y-4">

                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-sm text-blue-800">
                  <p>Current Quantity: <strong>{amendModal.item.quantity} {amendModal.item.unit}</strong></p>
                  <p className="text-xs text-blue-500 mt-1">This item is part of a production chain. Changing qty will update all pending stages safely.</p>
                </div>

                <div>
                  <label className="field-label">New Quantity *</label>
                  <div className="flex gap-2">
                    <input
                      type="number" min="1"
                      value={amendNewQty}
                      onChange={e => { setAmendNewQty(e.target.value); setAmendImpact(null); }}
                      className="field-input flex-1"
                      placeholder="Enter new quantity"
                    />
                    <button
                      onClick={checkAmendImpact}
                      disabled={amendChecking}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium disabled:opacity-50 whitespace-nowrap"
                    >
                      {amendChecking ? 'Checking...' : 'Check Impact'}
                    </button>
                  </div>
                </div>

                {amendImpact && (
                  <div className={`rounded-xl border p-4 space-y-3 ${amendImpact.can_proceed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{amendImpact.can_proceed ? '✅' : '🚫'}</span>
                      <div>
                        <p className={`font-semibold text-sm ${amendImpact.can_proceed ? 'text-green-800' : 'text-red-800'}`}>
                          {amendImpact.can_proceed
                            ? `Safe to change: ${amendImpact.current_qty} → ${amendImpact.new_qty} (${amendImpact.diff > 0 ? '+' : ''}${amendImpact.diff})`
                            : `Cannot proceed`}
                        </p>
                        {amendImpact.block_reason && (
                          <p className="text-xs text-red-600 mt-0.5">{amendImpact.block_reason}</p>
                        )}
                      </div>
                    </div>

                    {amendImpact.stage_impact?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">Stage-wise Impact:</p>
                        <div className="space-y-1.5">
                          {amendImpact.stage_impact.map((s, i) => (
                            <div key={i} className="bg-white rounded-lg px-3 py-2 border border-gray-100 text-xs flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <span className="font-medium text-gray-700">Stage {s.stage}: </span>
                                <span className="text-gray-500">{s.title}</span>
                                {s.department && <span className="text-gray-400"> · {s.department}</span>}
                              </div>
                              <div className="shrink-0 text-right">
                                <span className={`font-semibold px-1.5 py-0.5 rounded ${
                                  s.status === 'completed' ? 'bg-green-100 text-green-700' :
                                  s.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>{s.status}</span>
                                <span className="ml-2 text-gray-500">
                                  {s.assigned} → {s.new_assigned}
                                  {s.change !== 0 && (
                                    <span className={s.change > 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                                      {' '}({s.change > 0 ? '+' : ''}{s.change})
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-400 mt-2">* Completed stages will not be changed</p>
                      </div>
                    )}
                  </div>
                )}

                {amendImpact?.can_proceed && (
                  <div>
                    <label className="field-label">Reason for Change * <span className="text-gray-400 font-normal">(saved in item notes)</span></label>
                    <textarea
                      value={amendReason}
                      onChange={e => setAmendReason(e.target.value)}
                      rows={2}
                      className="field-input"
                      style={{ resize: 'none' }}
                      placeholder="e.g. Client revised order from 100 to 105 units"
                    />
                  </div>
                )}

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => { setAmendModal(null); setAmendImpact(null); }}
                    className="flex-1 border border-gray-200 text-gray-600 hover:bg-gray-50 py-2.5 rounded-xl text-sm font-medium"
                  >
                    Cancel
                  </button>
                  {amendImpact?.can_proceed && (
                    <button
                      onClick={confirmAmend}
                      disabled={!amendReason.trim() || amendLoading}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {amendLoading ? 'Updating...' : `Confirm: ${amendModal.item.quantity} → ${amendNewQty}`}
                    </button>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
